import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import Experience from './Experience.js'

export default class Camera
{
    constructor()
    {
        this.experience = new Experience()
        this.sizes = this.experience.sizes
        this.scene = this.experience.scene
        this.canvas = this.experience.canvas
        this.debug = this.experience.debug

        this.orbitEnabled = false

        this.setInstance()
        this.setOrbitControls()
        this.setParallax()
        this.setDebug()
    }

    setInstance()
    {
        this.instance = new THREE.PerspectiveCamera(35, this.sizes.width / this.sizes.height, 0.1, 100)
        this.instance.position.set(6, 4, 8)
        this.scene.add(this.instance)
    }

    setOrbitControls()
    {
        this.controls = new OrbitControls(this.instance, this.canvas)
        this.controls.enableDamping = true
        this.controls.enabled = false
        this.canvas.style.touchAction = 'auto'
    }

    setParallax()
    {
        // Mouse parallax as a subtle orbit around the model center. It rotates a
        // dedicated pivot node that wraps the glTF camera; the animation mixer
        // never touches this pivot, so it can't interfere with the scroll
        // animation or the camera path. The rotation is set absolutely every
        // frame (idempotent -> no drift/accumulation).
        this.parallax = {
            enabled: true,
            intensity: 0.06,
            ease: 0.06,
            target: new THREE.Vector2(0, 0),
            current: new THREE.Vector2(0, 0)
        }

        this.parallaxPivot = null

        window.addEventListener('mousemove', (event) =>
        {
            this.setParallaxTarget(
                (event.clientX / this.sizes.width) * 2 - 1,
                (event.clientY / this.sizes.height) * 2 - 1
            )
        })

        window.addEventListener('mouseleave', () =>
        {
            this.setParallaxTarget(0, 0)
        })
    }

    setParallaxTarget(x, y)
    {
        if(!this.parallax?.enabled)
            return

        this.parallax.target.set(x, y)
        this.experience.needsUpdate = true
    }

    setParallaxPivot(camera)
    {
        // Insert a pivot between the camera and its parent, keeping local
        // transforms untouched (parent is at the scene origin), so the scroll
        // animation keeps writing the same values to the camera.
        const parent = camera.parent
        if(!parent)
            return

        this.parallaxPivot = new THREE.Group()
        this.parallaxPivot.name = 'ParallaxPivot'
        parent.add(this.parallaxPivot)
        this.parallaxPivot.add(camera)
    }

    updateParallax()
    {
        if(!this.parallax || !this.parallax.enabled)
            return

        this.parallax.current.lerp(this.parallax.target, this.parallax.ease)

        if(this.parallax.current.distanceTo(this.parallax.target) > 0.0005)
            this.experience.needsUpdate = true
    }

    applyParallax()
    {
        if(!this.parallaxPivot)
            return

        if(!this.parallax.enabled || this.parallax.intensity === 0)
        {
            this.parallaxPivot.rotation.set(0, 0, 0)
            return
        }

        const yaw = -this.parallax.current.x * this.parallax.intensity
        const pitch = -this.parallax.current.y * this.parallax.intensity

        this.parallaxPivot.rotation.set(pitch, yaw, 0)
    }

    setDebug()
    {
        if(!this.debug.active)
            return

        const debugFolder = this.debug.ui.addFolder('camera')
        debugFolder.add(this, 'orbitEnabled').name('Orbit Controls').onChange((value) =>
        {
            this.controls.enabled = value
            this.canvas.style.touchAction = value ? 'none' : 'auto'
        })

        const parallaxFolder = debugFolder.addFolder('parallax')
        parallaxFolder.add(this.parallax, 'enabled').name('enabled')
        parallaxFolder.add(this.parallax, 'intensity').name('intensity').min(0).max(0.12).step(0.002)
        parallaxFolder.add(this.parallax, 'ease').name('ease').min(0.01).max(0.3).step(0.01)
    }

    setGltfCamera(camera)
    {
        this.instance = camera
        this.controls.object = camera
        this.setParallaxPivot(camera)
    }

    resize()
    {
        this.instance.aspect = this.sizes.width / this.sizes.height
        this.instance.updateProjectionMatrix()
    }

    update()
    {
        if(this.orbitEnabled)
        {
            this.controls.update()
        }

        this.applyParallax()
    }
}